import { Link } from "react-router-dom";
import { AcceptanceStatus } from "../../../constants";
import { DownOutlined } from "@ant-design/icons";
import { Button, Dropdown, Modal } from "antd";
import type { MenuProps } from "antd";
import { useMemo, useState } from "react";

export function LinkCellRenderer({ data, value }: any) {
  return (
    <Link
      className=""
      to={{
        pathname: `/organizations/${data.id}`
      }}
      data-testid="link-cell-renderer-link"
    >
      {value}
    </Link>
  );
}

export function StatusCellRenderer({ value, data, context }: any) {
  const btnCreateUser = () => {
    return (
      <Button
        data-testid="create-user-btn"
        onClick={() => {
          context?.setCreateUserDrawer?.({
            showInviteModel: true,
            inviteUserData: {
              email: data?.user?.email,
              phone: data?.user?.phone,
              id: data?.user?.id,
              first_name: data?.user?.first_name,
              last_name: data?.user?.last_name,
              company_id: data?.company_id
            }
          });
        }}
      >
        Create User
      </Button>
    );
  };
  switch (value) {
    case AcceptanceStatus.ACTIVE:
      return <p className="margin-0 captialize-first text-green-600">Active</p>;
    case AcceptanceStatus.DEACTIVATED:
      return (
        <p className="margin-0 captialize-first text-red-600">Deactivated</p>
      );
    case AcceptanceStatus.NOT_INVITED:
      return (
        <div className="flex items-center justify-between gap-2">
          <p className="!m-0 captialize-first text-red-600">Not Invited</p>
          {btnCreateUser()}
        </div>
      );
    case AcceptanceStatus.LOCKED:
      return <p className="margin-0 captialize-first text-red-600">Locked</p>;
    default:
      return (
        <div className="flex items-center justify-between gap-2">
          <p className="!m-0 captialize-first text-red-600">Invited</p>
          {btnCreateUser()}
        </div>
      );
  }
}

export function ProjectFeatureLinkCellRenderer(param: any) {
  const { data, value, context, colDef } = param;

  let featureId = 0;
  switch (colDef.headerName) {
    case "Submittal":
      featureId = 1;
      break;
    case "Submittal DateBlock":
      featureId = 2;
      break;
    case "Material":
      featureId = 3;
      break;
    case "Material DateBlock":
      featureId = 4;
      break;
    default:
      break;
  }

  if (value > 0) {
    const linkObj = {
      pathname: `/organizations/${context?.orgId}/subscriptions/${context?.subId}/project/${context?.projectId}/events/project`,
      state: {
        name: data?.name,
        userId: data?.id,
        projectId: context.projectId,
        featureId,
        organization: context?.organization,
        subscription: context?.subscription,
        featureName: colDef.headerName
      }
    };

    return (
      <Link to={linkObj} data-testid="link-cell-renderer-link-1">
        {value}
      </Link>
    );
  }

  return value;
}

export function LoginFeatureLinkCellRenderer(params: any) {
  const { data, value, context } = params;
  if (value > 0) {
    const linkObj = {
      pathname: `/organizations/${context?.orgId}/subscriptions/${context?.subId}/project/${context?.projectId}/events/login`,
      state: {
        name: data?.name,
        userId: data?.id,
        organization: context?.organization,
        subscription: context?.subscription
      }
    };

    return (
      <Link to={linkObj} data-testid="link-cell-renderer-link-2">
        {value}
      </Link>
    );
  }

  return value;
}

export function UserStatusAtSystemLevelCellRenderer({
  value,
  context,
  data
}: any) {
  const [isLoading, setIsLoading] = useState(false);
  const { hasAdminAccess, setUnlockUserDrawer, unlockUserAccount } = context;

  const unlockUserMenu: MenuProps = useMemo(() => {
    const firstName = data?.user?.first_name ?? "";
    const lastName = data?.user?.last_name ?? "";
    const displayName = `${firstName} ${lastName}`.trim() || "this user";
    const email = data?.user?.email ?? "";

    return {
      items: [
        {
          key: "unlock_with_email",
          label: "Unlock by email",
          onClick: () => {
            Modal.confirm({
              title: "Unlock by email",
              content: (
                <div>
                  <p className="mb-2">
                    This will unlock the account and send an email to the user.
                    Continue?
                  </p>
                  <p className="mb-0 font-medium">{displayName}</p>
                  {email ? (
                    <p className="mb-0 text-sm text-gray-600">{email}</p>
                  ) : null}
                </div>
              ),
              okText: "Unlock",
              cancelText: "Cancel",
              async onOk() {
                setIsLoading(true);
                try {
                  await unlockUserAccount?.(email);
                } finally {
                  setIsLoading(false);
                }
              }
            });
          }
        },
        {
          key: "unlock_with_password",
          label: "Unlock with new password",
          onClick: () => {
            setUnlockUserDrawer?.({
              showUnlockModel: true,
              unlockUserData: {
                email: data?.user?.email,
                id: data?.user?.id,
                first_name: data?.user?.first_name,
                last_name: data?.user?.last_name
              }
            });
          }
        }
      ]
    };
  }, [data?.user, setUnlockUserDrawer, unlockUserAccount]);

  if (hasAdminAccess && value === AcceptanceStatus.LOCKED) {
    return (
      <div className="flex items-center gap-2">
        <div className="margin-0 captialize-first text-red-900">Locked</div>

        <Dropdown
          menu={unlockUserMenu}
          trigger={["click"]}
          disabled={isLoading}
        >
          <Button
            data-testid="unlock-user-btn"
            loading={isLoading}
            disabled={isLoading}
          >
            Unlock User <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    );
  }

  switch (value) {
    case AcceptanceStatus.ACTIVE:
      return <p className="margin-0 captialize-first text-green-600">Active</p>;
    case AcceptanceStatus.DEACTIVATED:
      return (
        <p className="margin-0 captialize-first text-red-600">Deactivated</p>
      );
    case AcceptanceStatus.LOCKED:
      return <p className="margin-0 captialize-first text-red-900">Locked</p>;
    default:
      return <p className="captialize-first text-red-600">Pending</p>;
  }
}
